import { schemaImage } from 'apps/website/src/morphismSchema/components/schemaImage';
import { schemaDatoButton } from 'apps/website/src/morphismSchema/schemaDatoButton';
import { Subpath } from 'apps/website/src/utils/linkResolver';
import { Image } from 'libs/growth-platform-brand-system-v2/src/components/Image';
import { fontCaptionMedium } from 'libs/growth-platform-brand-system-v2/src/styles/typographyNew';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import { ButtonTracking } from 'libs/growth-platform-brand-system-v2/src/utils/tracking/ButtonTracking';
import morphism from 'morphism';
import React from 'react';
import { ImageAnimateOnHover } from './ImageAnimateOnHover';
import { SectionBottom } from './SectionBottom';
import { SubMenuContentProps } from './types';
import { getMenuTrackEvent } from 'libs/growth-platform-brand-system-v2/src/utils/tracking';
import { ImageProps } from '@payfit/growth-platform-brand-system-v2/components/Image/types';

export const isBlogArticle = (__typename?: string) => {
  const allowTypes = [
    'DatoCmsCategoryPage',
    'DatoCmsSubCategoryPage',
    'DatoCmsBlogArticle',
  ];

  return allowTypes.includes(__typename || '');
};

export const isClientLink = (__typename?: string) => {
  const allowTypes = ['DatoCmsClientPage'];

  return allowTypes.includes(__typename || '');
};

export const getSubPath = (__typename?: string): Subpath | undefined => {
  if (isBlogArticle(__typename)) {
    return Subpath.BlogArticles;
  }
  if (isClientLink(__typename)) {
    return Subpath.Client;
  }
  return;
};

export const SubMenuContent = ({
  submenuColumns,
  sectionBottom,
  index,
  locale,
}: SubMenuContentProps) => {
  if (!submenuColumns) return null;

  let maxWidthClass;
  if (submenuColumns?.length === 1) {
    maxWidthClass = 'xl:max-w-[calc(340px+40px+40px)]';
  } else if (submenuColumns.length === 2) {
    maxWidthClass = 'xl:max-w-[calc(340px*2+32px+40px+40px)]';
  } else {
    maxWidthClass = 'xl:max-w-[calc(340px*3+32px*2+40px+40px)]';
  }

  return (
    <div
      className={classNames(
        'SubMenuContent',
        'xl:max-h-[calc(100vh-100px)] xl:overflow-scroll',
        'bg-white',
        'xl:rounded-[20px]',
        'flex flex-col gap-40 xl:gap-0',
        'w-full',
      )}
      role="menu"
      aria-label="Submenu"
    >
      <div
        className={classNames(
          'xl:p-40',
          'grid grid-cols-[repeat(auto-fill,minmax(340px,1fr))]',
          'gap-32',
          maxWidthClass,
        )}
      >
        {submenuColumns.map((column, columnIndex) => (
          <div
            key={columnIndex}
            className={classNames(
              'flex',
              'flex-col',
              'gap-y-32',
              'w-full',
              'xl:w-[340px]',
            )}
          >
            {column.submenuSections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                data-tracking-section={section.title}
                className={classNames(
                  'SubenuSection',
                  'w-full',
                  section.backgroundColor === 'blue'
                    ? 'bg-blue-21'
                    : 'bg-neutral-1',
                )}
              >
                <div
                  className={classNames(
                    'text-navy-60',
                    fontCaptionMedium,
                    'text-left',
                    'border-b',
                    'border-silver-50',
                    'max-w-full',
                    'mb-16 pb-16 xl:pt-0',
                    'SubmenuSectionTitle',
                  )}
                >
                  {section.title}
                </div>
                <div className={classNames('flex flex-col', 'gap-y-12')}>
                  {section.submenuItems.map((submenuItem, itemIndex) => {
                    const sectionID = `header_${index}_${sectionIndex}_submenu_${columnIndex}`;
                    const { as, __typename, color, variant, ...linkProps } =
                      morphism(schemaDatoButton, {
                        ...submenuItem,
                        isBlogArticle: isBlogArticle(
                          submenuItem.internalUrl?.__typename,
                        ),
                        isClientLink: isClientLink(
                          submenuItem.internalUrl?.__typename,
                        ),
                        locale,
                      });
                    const trackEvent = getMenuTrackEvent({
                      locale,
                      menuItem: submenuItem,
                    });

                    const _image = {
                      ...morphism(schemaImage, {
                        ...submenuItem.image,
                        pixelDensity: 'image_with_text',
                      }),
                      sizes: '56px',
                    } as ImageProps;

                    return (
                      <ButtonTracking
                        component={as}
                        key={itemIndex}
                        {...linkProps}
                        className={classNames(
                          'w-full',
                          'flex',
                          submenuItem.subtitle ? 'gap-x-16' : 'gap-x-8',
                          'items-center',
                          'xl:hover:bg-blue-21',
                          'xl:hover:rounded-[10px]',
                          'p-4',
                        )}
                        section={sectionID}
                        index={itemIndex}
                        labelTracking={submenuItem.title}
                        trackEvent={trackEvent}
                      >
                        {submenuItem?.gif && submenuItem?.image && (
                          <ImageAnimateOnHover submenuItem={submenuItem} />
                        )}
                        {!submenuItem?.gif && submenuItem?.image && (
                          <Image
                            {..._image}
                            className={classNames(
                              'w-full',
                              submenuItem.subtitle
                                ? 'h-56 max-w-[56px]'
                                : 'h-[30px] max-w-[30px]',
                            )}
                            alt="ressource-image"
                          />
                        )}
                        <div
                          className={classNames(
                            'TitleAndSubtitle',
                            'flex flex-col items-start gap-4',
                            'mr-56',
                            'text-left',
                            submenuItem.subtitle ? 'pb-8 pt-8' : 'pb-0',
                            'w-full',
                            'whitespace-normal',
                          )}
                        >
                          <div
                            className={classNames(
                              'text-neutral-301',
                              'text-[14px]',
                              'font-medium',
                              'leading-5',
                            )}
                          >
                            {submenuItem.title}
                          </div>
                          {submenuItem.subtitle && (
                            <div
                              className={classNames(
                                'text-navy-90',
                                'text-[12px]',
                                'font-normal',
                                'leading-[18px]',
                              )}
                            >
                              {submenuItem.subtitle}
                            </div>
                          )}
                        </div>
                      </ButtonTracking>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      {sectionBottom && (
        <SectionBottom
          sectionBottom={sectionBottom}
          index={index}
          locale={locale}
        />
      )}
    </div>
  );
};
